import {
  Box,
  Button,
  Center,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';

import { ImageFallback } from '@/imports/ui/chakra/components/ImageFallback';
import { CameraIcon } from '@/imports/ui/chakra/feather';
import { useObjectURL } from '@/imports/ui/hooks/useObjectURL';

type ImageUploadModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  height: number;
  width: number;
  onSubmit: (files: File[]) => void;
  isLoading?: boolean;
  objectFit?: 'cover' | 'contain';
  showSizeErrorToast?: boolean;
};

export const ImageUploadModal: React.FC<ImageUploadModalProps> = (props) => {
  const toast = useToast();
  const [files, setFiles] = React.useState<File[]>([]);
  const aspectRatio = `${props.width / props.height}`;
  const objectURL = useObjectURL(files[0]);

  const MAX_IMAGE_UPLOAD_SIZE_BYTES = 15 * 1024 * 1024;
  const IMAGE_FILE_TOO_LARGE_MESSAGE =
    `This file exceeds the maximum size of ${MAX_IMAGE_UPLOAD_SIZE_BYTES / (1024 * 1024)} MB. Please choose an image under the size limit and try again.`;
    
  const dropzoneOptions = {
    accept: 'image/*',
    noClick: true,
    noKeyboard: true,
    onDrop: setFiles,
    ...(props.showSizeErrorToast
      ? {
          maxSize: MAX_IMAGE_UPLOAD_SIZE_BYTES,
          onDropRejected: () => {
            toast({
              title: IMAGE_FILE_TOO_LARGE_MESSAGE,
              status: 'error',
            });
          },
        }
      : {}),
  };

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone(
    dropzoneOptions
  );

  React.useEffect(() => {
    !props.isOpen && setFiles([]);
  }, [props.isOpen]);

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader pb="1" textAlign="center">
          {props.title}
        </ModalHeader>
        <ModalBody py="0">
          <Text textStyle="compact" color="gray.600" textAlign="center" mb="4">
            {props.subtitle}
          </Text>

          {!!files.length && (
            <Center w="full">
              <Center
                sx={{ aspectRatio }}
                bg="gray.50"
                maxW={props.width / 2}
                w="full"
              >
                <Image
                  alt=""
                  mx="auto"
                  w="full"
                  maxW={props.width / 2}
                  borderRadius="base"
                  objectFit={props.objectFit}
                  sx={{ aspectRatio }}
                  src={objectURL}
                  fallback={
                    <ImageFallback
                      mx="auto"
                      w="full"
                      maxW={props.width / 2}
                      borderRadius="base"
                      aspectRatio={aspectRatio}
                    />
                  }
                />
              </Center>
            </Center>
          )}

          <Box
            border="1px"
            borderColor={isDragActive ? 'gray.300' : 'transparent'}
            mx="auto"
            display={!!files.length ? 'none' : 'block'}
            {...getRootProps()}
            bg="gray.50"
            maxW={props.width / 2}
            sx={{ aspectRatio }}
            borderRadius="base"
            p="3"
          >
            <input {...getInputProps()} />
            <Center h="full" textAlign="center" color="gray.600">
              {isDragActive ? 'Drop the image here' : 'Drag & Drop'}
            </Center>
          </Box>

          <HStack spacing="2" my="4" justifyContent="center">
            <Button
              variant="outline"
              colorScheme="gray"
              leftIcon={<CameraIcon />}
              onClick={open}
            >
              {!!files.length ? 'Choose A Different Image' : 'Choose Image'}
            </Button>
            {!!files.length && (
              <Button variant="ghost" onClick={() => setFiles([])}>
                Remove Image
              </Button>
            )}
          </HStack>
        </ModalBody>
        <ModalFooter borderTop="1px" borderTopColor="gray.100">
          <HStack spacing="2">
            <Button
              variant="outline"
              colorScheme="gray"
              onClick={props.onClose}
              isDisabled={props.isLoading}
            >
              Cancel
            </Button>
            <Button
              isLoading={props.isLoading}
              isDisabled={!files.length}
              onClick={() => props.onSubmit(files)}
            >
              Save
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

ImageUploadModal.defaultProps = {
  objectFit: 'cover',
};
